import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {apiRequest} from '../../utils/AxiosHelper';
import {useAppSelector} from '../hooks';
import {PaginationParams} from "../../types";
import API from "../../modules/settings/APIs";

export const queryKeys = {
  issues: ['issues'] as const,
  approval: ['approval'] as const,
  issuesFiltered: (filterId: number, pagination?: PaginationParams) => ['issues', 'filtered', filterId, pagination] as const,
  issueDetail: (id: string) => ['issues', 'detail', id] as const,
  user: ['user'] as const,
} as const;

// Custom hook for fetching a single issue by ID
export const useIssueDetail = (id?: string) => {
  const {isAuthenticated, token} = useAppSelector((state) => state.session);

  return useQuery({
    queryKey: queryKeys.issueDetail(id || ''),
    queryFn: async () => {
      if (!id) return null;
      const response = await apiRequest.get(API.issues.operations.get(id));
      return response.data.data || response.data;
    },
    staleTime: 2 * 60 * 1000,
    enabled: !!id && isAuthenticated && !!token,
  });
};

// Custom hook for creating issues
export const useCreateIssue = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest.post(API.issues.operations.create, {
        data: data
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: queryKeys.issues});
    },
  });
};

// Custom hook for updating issues
export const useUpdateIssue = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({id, data}: { id: string; data: any }) => {
      const response = await apiRequest.post(API.issues.operations.update, {
        id: id,
        data: data
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: queryKeys.issues});
    },
  });
};

// Custom hook for deleting issues
export const useDeleteIssue = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest.delete(`/issues/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: queryKeys.issues});
    },
  });
};

// Custom hook for approve issue
export const useApproveIssue = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest.post(API.issues.operations.approve, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: queryKeys.issues});
    },
  });
};
